import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Plus, X, Trash, Siren, Clock } from "@phosphor-icons/react";

export default function EmergencyProtocol() {
  const [shifts, setShifts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, c, w] = await Promise.all([
        api.get("/emergency-protocol/on-call"),
        api.get("/emergency-protocol/current-oncall"),
        api.get("/workers").catch(() => ({ data: [] })),
      ]);
      setShifts(s.data || []);
      setCurrent(c.data);
      setWorkers(w.data || []);
    } catch { toast.error("Failed to load on-call schedule"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (sid) => {
    if (!window.confirm("Remove this on-call shift?")) return;
    try { await api.delete(`/emergency-protocol/on-call/${sid}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="emergency-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <Siren weight="fill" className="text-red-500"/> Emergency After-Hours Protocol
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              On-call schedule and escalation ladder. When a tenant reports a burst pipe at 2 AM, dispatch knows exactly who to page.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-shift-btn"
            className="h-11 px-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
            <Plus size={14}/> Add On-Call Shift
          </button>
        </div>

        <div className="rugged-card p-4 border-l-8 border-red-500" data-testid="who-oncall-now">
          <div className="flex items-center gap-2 mb-3">
            <Siren size={22} className="text-red-600"/>
            <h3 className="font-black uppercase tracking-widest text-sm">Who's On Call Right Now</h3>
            <span className="ml-auto text-[10px] text-slate-500">as of {fmt(current?.at)}</span>
          </div>
          {current?.levels?.length > 0 ? (
            <div className="space-y-2">
              {current.levels.map(lvl => (
                <div key={lvl.level} className="p-3 border-2 border-slate-200 bg-slate-50" data-testid={`level-${lvl.level}`}>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                    Escalation Level {lvl.level}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {lvl.techs.map(t => (
                      <div key={t.id} className="px-3 py-2 bg-white border-2 border-slate-900 shadow-[2px_2px_0_#0f172a]">
                        <div className="font-black text-sm">{t.tech_name}</div>
                        {t.phone && (
                          <a href={`tel:${t.phone}`} data-testid={`call-${t.id}`}
                            className="text-[10px] text-cyan-700 hover:underline inline-flex items-center gap-1">
                            <Phone size={10}/> {t.phone}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-amber-50 border-l-4 border-amber-500 text-sm">
              <b>Nobody is on call right now.</b> Add a shift below.
            </div>
          )}
        </div>

        <div className="rugged-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={20} className="text-slate-600"/>
            <h3 className="font-black uppercase tracking-widest text-sm">Full On-Call Schedule</h3>
          </div>
          {busy ? <div className="text-sm text-slate-500 py-4">Loading…</div>
          : shifts.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No shifts scheduled yet.</div>
          ) : (
            <div className="space-y-2">
              {shifts.map(s => (
                <div key={s.id} className="p-3 border-2 border-slate-200 flex items-center gap-3 flex-wrap" data-testid={`shift-row-${s.id}`}>
                  <div className={`px-2 h-8 leading-8 font-black uppercase tracking-widest text-[10px] ${
                    s.escalation_level === 1 ? "bg-red-600 text-white" :
                    s.escalation_level === 2 ? "bg-amber-500 text-white" :
                    "bg-slate-600 text-white"
                  }`}>L{s.escalation_level}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-sm">{s.tech_name}</div>
                    <div className="text-[10px] text-slate-500">
                      {fmt(s.starts_at)} → {fmt(s.ends_at)}
                    </div>
                    {s.notes && <div className="text-[10px] text-slate-700 mt-0.5">{s.notes}</div>}
                  </div>
                  {s.phone && (
                    <a href={`tel:${s.phone}`} data-testid={`shift-call-${s.id}`}
                      className="h-8 px-2 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                      <Phone size={12}/> {s.phone}
                    </a>
                  )}
                  <button onClick={() => del(s.id)} data-testid={`shift-delete-${s.id}`}
                    className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                    <Trash size={14}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {showNew && (
          <ShiftModal workers={workers} onClose={() => setShowNew(false)}
            onSaved={() => { setShowNew(false); load(); }}/>
        )}
      </div>
    </AdminShell>
  );
}

function ShiftModal({ workers, onClose, onSaved }) {
  const [form, setForm] = useState({
    tech_id: "", starts_at: "", ends_at: "", escalation_level: 1, phone: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.tech_id || !form.starts_at || !form.ends_at) return toast.error("Tech + times required");
    setBusy(true);
    try {
      await api.post("/emergency-protocol/on-call", {
        ...form,
        escalation_level: parseInt(form.escalation_level) || 1,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      });
      toast.success("Shift added");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="new-shift-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] max-h-[90vh] overflow-y-auto">
        <div className="p-3 bg-red-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Add On-Call Shift</div>
          <button onClick={onClose} data-testid="new-shift-close" className="p-1 hover:bg-red-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <select value={form.tech_id} onChange={e => setForm({...form, tech_id: e.target.value})} data-testid="shift-tech-select"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
            <option value="">— Select tech —</option>
            {workers.map(w => <option key={w.id || w._id} value={w.id || w._id}>{w.name || w.email}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
              Starts at
              <input type="datetime-local" value={form.starts_at} onChange={e => setForm({...form, starts_at: e.target.value})} data-testid="shift-starts"
                className="h-10 px-2 border-2 border-slate-300 text-sm"/>
            </label>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
              Ends at
              <input type="datetime-local" value={form.ends_at} onChange={e => setForm({...form, ends_at: e.target.value})} data-testid="shift-ends"
                className="h-10 px-2 border-2 border-slate-300 text-sm"/>
            </label>
          </div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
            Escalation level (1 = primary, 2 = backup, 3+ = further backup)
            <select value={form.escalation_level} onChange={e => setForm({...form, escalation_level: e.target.value})} data-testid="shift-level"
              className="h-10 px-2 border-2 border-slate-300 text-sm">
              {[1,2,3,4,5].map(n => <option key={n} value={n}>Level {n}</option>)}
            </select>
          </label>
          <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Emergency phone"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes (e.g., 'has key to lockbox at 4241')"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !form.tech_id} data-testid="shift-save-btn"
            className="w-full h-11 bg-red-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Add Shift"}
          </button>
        </div>
      </div>
    </div>
  );
}
